<template>
  <div id="pos-settings-modal" class="settings-dialog modal-footer">
    <SDialog v-model:open="modalVisibility">
      <SDialogScrollContent
        class="settings-dialog !min-w-[750px] !max-w-[1000px] !h-[70vh]"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle>{{ $t("settings.title") }}</SDialogTitle>
        </SDialogHeader>
        <div class="content">
          <ScanSettings ref="scanSettings"></ScanSettings>
          <TicketsEmails ref="ticketsEmails"></TicketsEmails>
          <DeviceSettings
            ref="deviceSettings"
            v-if="!hasHardwareInfo"
          ></DeviceSettings>
          <Suspense>
            <DeviceLocationSettings
              v-if="hasHardwareInfo"
              ref="DeviceLocationSettings"
            >
            </DeviceLocationSettings>
          </Suspense>

          <CashDrawerSettings ref="cashDrawerSettings"></CashDrawerSettings>

          <CompleteWithBalanceSettings
            ref="CompleteWithBalanceSettings"
          ></CompleteWithBalanceSettings>
          <Suspense>
            <TerminalsStatus></TerminalsStatus>
          </Suspense>

          <SettingsPaymentMethods
            ref="settingsPaymentMethods"
          ></SettingsPaymentMethods>
        </div>
        <SDialogFooter class="gap-3">
          <SButton
            type="button"
            variant="outline"
            class="min-w-[120px]"
            @click="hideDialog"
          >
            {{ $t("common.buttons.cancel") }}
          </SButton>
          <SButton
            type="button"
            class="min-w-[120px]"
            :loading="isLoading"
            :disabled="!canSave"
            @click="onSave"
            id="save-settings-button"
          >
            {{ $t("common.buttons.save") }}
          </SButton>
        </SDialogFooter>
      </SDialogScrollContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

// modules
import POSConfigModule from "@/store/modules/pos-config";
import PrinterConfigModule from "@/store/modules/printers-config";

// helpers
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";

// constants
import { ModalStatus } from "@/constants/modal";
import { IPaymentConfig, PaymentMethodTypes } from "@/types/db/pos-config";
import { Currencies } from "@/constants/currency";
import { IPrinterUsage } from "@/interfaces/printer/printer";

// services
import storage, { LocalStorageKeys } from "@/services/storage.service";
import { DeviceInfo } from "@/types/gql/device";
import DeviceModule from "@/store/modules/device";
import { computed } from "vue";
import { LogLevel, LogService } from "@/services/logging";

import ScanSettings from "@/components/modal/settings/ScanSettings.vue";
import TerminalsStatus from "@/components/modal/settings/TerminalsStatus.vue";
import {
  SButton,
  SDialog,
  SDialogFooter,
  SDialogHeader,
  SDialogScrollContent,
  SDialogTitle,
} from "@smeetz/ui-core";

import { setDeviceLocation } from "@/api/device/device";
import NotificationService from "@/services/notification.service";
import { getDeviceLocation } from "@/api/gql/device";

export default defineComponent({
  name: "Settings",
  components: {
    ScanSettings,
    TerminalsStatus,
    SButton,
    SDialog,
    SDialogFooter,
    SDialogHeader,
    SDialogScrollContent,
    SDialogTitle,
  },
  async setup() {
    const modalVisibility = dialogVisibleForStatus(ModalStatus.Settings);
    let isLoading = ref<boolean>(false);

    const settingsPaymentMethods = ref();
    const ticketsEmails = ref();
    const scanSettings = ref();
    const deviceSettings = ref();
    const DeviceLocationSettings = ref();
    const CompleteWithBalanceSettings = ref();
    const cashDrawerSettings = ref();

    const cardPaymentMethod = computed(() => {
      return settingsPaymentMethods.value?.settingsData.find(
        (elm: any) => elm.id === PaymentMethodTypes.CardAuto
      );
    });

    const hasHardwareInfo = computed(() => {
      // If there is no hardware info (user using a browser) we don't show device settings
      if (DeviceModule.hardwareInfo) {
        return true;
      } else {
        return false;
      }
    });

    const canSave = computed(() => {
      return (
        !cardPaymentMethod.value ||
        !cardPaymentMethod.value.used ||
        (cardPaymentMethod.value.used && cardPaymentMethod.value.terminal)
      );
    });

    const onSave = async () => {
      isLoading.value = true;

      let paymentConfig: IPaymentConfig = {} as IPaymentConfig;
      let paymentFormData = settingsPaymentMethods.value?.settingsData || [];
      LogService.info(`User confirmed attached settings updates`, {
        paymentFormData: paymentFormData.value,
      });

      const printerOption: IPrinterUsage = ticketsEmails.value
        ?.printerOption as IPrinterUsage;
      // const deviceId: number = deviceSettings.value?.deviceId as number;
      const deviceId: number = DeviceLocationSettings.value?.deviceId as number;

      let cardPaymentMethod = paymentFormData.find(
        (elm: any) => elm.id === PaymentMethodTypes.CardAuto
      );
      // Check if the user didn't select a terminal then disable Card payment method
      if (!cardPaymentMethod.terminal) cardPaymentMethod.used = false;

      paymentFormData.map(
        (item: {
          id: PaymentMethodTypes;
          used: boolean;
          currency?: Currencies[];
          paymentMethods?: string[];
          terminal?: string;
        }) => {
          const {
            id = PaymentMethodTypes.Cash,
            used = true,
            currency = null,
            paymentMethods = null,
            terminal = null,
          } = item;

          if (terminal) {
            paymentConfig.terminal = terminal;
          }

          (paymentConfig as Record<PaymentMethodTypes, any>)[id] = {
            used,
            ...(currency && { acceptedCurrencies: currency }),
            ...(paymentMethods && { paymentMethods }),
            ...(id === PaymentMethodTypes.CardAuto && { processor: "ADYEN" }),
          };
        }
      );

      // If we have a hardware and no location for the device, and a device selected by the user
      // we should patch device so that backend will attach this hardware to a device with location in backend

      // If we don't even have a hardware info it means we're in a browser, we will keep old logic

      if (DeviceModule.hardwareInfo) {
        // we are in a desktop app, not a browser
        if (!DeviceModule.hardwareLocation) {
          // so this hardware is being used for the first time and user wants to assign it to
          // a device with location in backend
          LogService.info(
            `App running on device with hardwareInfo but no location linked.`
          );
          console.log(
            "going to link this hardware to  a device with location in backend"
          );
          // going to link this hardware to  a device with location in backend
          // lets prepare the payload
          const selectedDevice: DeviceInfo | undefined =
            DeviceModule.devices.find((device) => device.id === deviceId);
          // console.log("selectedDevice");
          // console.log(selectedDevice);
          if (selectedDevice && POSConfigModule.groupId) {
            const stats = null; // TODO Hamza: Ask Saoussene what this is. I found it in POSTman
            // Prefer v2 hash (MAC excluded, stable across network changes).
            // Fall back to v1 hash, then to the legacy hardwareInfo.id.
            const hardwareUid =
              DeviceModule.getHardwareHashV2 ||
              DeviceModule.getHardwareHash ||
              DeviceModule.hardwareInfo.id;
            const deviceId = selectedDevice.id;
            const fiskalyCashRegisterId = selectedDevice.fiskalyCashRegisterId;
            const name = selectedDevice.name;
            const status = 1;
            const groupId = POSConfigModule.groupId;
            LogService.info(
              `Going to link the device to location in backend using PATCH device payload attached.`,
              {
                params: {
                  stats,
                  hardwareUid,
                  fiskalyCashRegisterId: String(fiskalyCashRegisterId),
                  name: String(name),
                  status,
                  groupId: Number(groupId),
                  deviceId: Number(deviceId),
                },
              }
            );
            try {
              const resp = await setDeviceLocation({
                stats,
                hardwareUid: hardwareUid,
                fiskalyCashRegisterId: String(fiskalyCashRegisterId),
                name: String(name),
                status,
                groupId: Number(groupId),
                deviceId: Number(deviceId),
              });
              LogService.info(
                `Device is now linked to location in backend, response attached.`,
                { params: resp }
              );

              // console.log("resp");
              // console.log(resp);
              // Lets set the location assigned with this hardware to the state
              // DeviceModule.setHardwareLocation({
              //   name: resp.name,
              //   id: resp.id,
              // });
              if (resp) {
                // so machine was linked to the device successfully

                /////
                const device = await getDeviceLocation(
                  /*"decommision_device"*/ hardwareUid,
                  POSConfigModule.groupId
                );
                LogService.info(
                  `Device location pulled from backend backend (attached). HardwareUid: ${hardwareUid}`,
                  {
                    params: device,
                  }
                );
                console.log("device");
                console.log(device);
                // If we get a device with a location, then we set the location to the state
                // and in the settings popup we show a disabled dropdown showning the current device
                // this location will be later used for Cash in and Cash out
                if (device && device.length) {
                  if (device[0].locationId) {
                    DeviceModule.setHardwareLocation({
                      locationId: device[0].locationId,
                      deviceName: String(device[0].name),
                      deviceId: Number(device[0].id),
                      deviceUid: String(device[0].deviceUid),
                      FiskalyCashRegisterId:
                        device[0].fiskalyCashRegisterId || "",
                    });
                  } else {
                    NotificationService.showCustomError(
                      "Error when setting the hardware"
                    );
                  }
                } else {
                  // if we get no device or we get a device without location then we pull all devices from backend
                  // and in the settings popup we show a dropdown with all the devices for the user to select one of them
                  // and once he submits his selection we call the api created by Saoussene and we supply the hardwareUuid
                  // we will do it in DeviceLocationSettings component
                  // LogService.info(
                  //   `Device isn't linked to a location in backend. Going to fetch devices from backend. HardwareUid: ${hardwareUid}`
                  // );
                  // await DeviceModule.fetchDevices();
                  NotificationService.showCustomError(
                    "Error when setting the hardware"
                  );
                }
                /////
              }
            } catch (err) {
              // TODO HAMZA: here we show an error message based on what we had in the response
              console.log("err");
              console.log(err);
              LogService.info(
                `Error when linking device to location in backend.`,
                { params: err },
                LogLevel.Error
              );
              NotificationService.showCustomError(
                "Error when setting the hardware"
              );
              // we don't close the popup
              return;
            }
          }
        }
      } else {
        // App is running without hardware info. Going to set a device selected by the user in old way (no location logic and no backend)
        // find device in devices array using deviceId
        const Id: number = deviceSettings.value?.deviceId as number;
        const device: DeviceInfo | undefined = DeviceModule.devices.find(
          (device) => device.id === Id
        );
        LogService.info(
          `App is running without hardware info. Going to set a device selected by the user (attached) in old way (no location logic and no backend)`,
          { params: device }
        );
        if (device) {
          // Save device info
          DeviceModule.setDeviceInfo(device);
        }
      }
      // Save printer options
      PrinterConfigModule.setPrinterOption(printerOption);

      POSConfigModule.postPaymentMethods(paymentConfig);
      POSConfigModule.setPaymentSettings(paymentFormData);

      // whether to allow completing unpaid/partially paid bookings or not
      POSConfigModule.setCompleteBookingWithBalance(
        CompleteWithBalanceSettings.value.status
      );

      // Save selected ticket type
      PrinterConfigModule.setTicketType(ticketsEmails.value.selectedTicketType);

      // save label printing status (enabled/disabled)
      PrinterConfigModule.setIsLabelPrintingEnabled(
        ticketsEmails.value.labelPrintingStatus
      );
      // save the selected label printer
      if (ticketsEmails.value.labelPrinter) {
        PrinterConfigModule.setLabelPrinter(ticketsEmails.value.labelPrinter);
      }
      // save the selected ticket printer
      if (ticketsEmails.value.ticketPrinter) {
        PrinterConfigModule.setTicketPrinter(ticketsEmails.value.ticketPrinter);
      }
      // save the selected receipt printer
      if (ticketsEmails.value.receiptPrinter) {
        PrinterConfigModule.setReceiptPrinter(
          ticketsEmails.value.receiptPrinter
        );
      }

      // save ticket printing status (enabled/disabled)
      PrinterConfigModule.setIsTicketPrintingEnabled(
        ticketsEmails.value.ticketPrintingStatus
      );

      // save auto print receipt status (auto/manual)
      PrinterConfigModule.setAutoPrintReceipt(
        cashDrawerSettings.value.autoPrintReceipt || false
      );

      POSConfigModule.setOpenCashDrawer(
        cashDrawerSettings.value.selectedOption
      );

      POSConfigModule.setAutoScanTickets(scanSettings.value.autoScanTickets);

      // Order summary receipt printing
      PrinterConfigModule.setIsOrderSummaryReceiptStatus(
        ticketsEmails.value.orderSummaryReceiptPrintingStatus || false
      );
      PrinterConfigModule.setOrderSummaryReceiptPrinter(
        ticketsEmails.value.orderSummaryReceiptPrinter || null
      );

      setTimeout(() => {
        isLoading.value = false;
        hideDialog();
      }, 500);
    };

    return {
      modalVisibility,
      isLoading,
      settingsPaymentMethods,
      ticketsEmails,
      deviceSettings,
      DeviceLocationSettings,
      TerminalsStatus,
      hideDialog,
      onSave,
      canSave,
      CompleteWithBalanceSettings,
      cashDrawerSettings,
      scanSettings,
      hasHardwareInfo,
      DeviceModule,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

.settings-dialog {
  .content {
    max-height: calc(70vh - 150px);
    overflow-y: scroll;
  }

  h2 {
    line-height: 29px;
    letter-spacing: 0px;
    color: black;
    font-weight: 700;
    font-style: normal;
    font-size: 24px;
    text-align: left;
  }
}
</style>
